import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Mountain, Waves, TreePine } from 'lucide-react';
import { Button } from '../ui/button';

const destinations = [
  {
    id: 'deomali',
    name: 'Deomali Peak',
    description: "Odisha's highest peak at 1,672m with breathtaking sunrise views over the Eastern Ghats",
    icon: Mountain,
    distance: '45 km',
    image: 'https://images.unsplash.com/photo-1758683713567-2c958d1ff99b?w=800&q=80',
  },
  {
    id: 'duduma',
    name: 'Duduma Falls',
    description: 'The 175-meter majestic waterfall on the Machkund River, known as the Niagara of Odisha',
    icon: Waves,
    distance: '80 km',
    image: 'https://images.pexels.com/photos/5675191/pexels-photo-5675191.jpeg?auto=compress&w=800',
  },
  {
    id: 'kolab',
    name: 'Kolab Reservoir',
    description: 'Serene dam reservoir surrounded by hills, perfect for boating, birdwatching and sunsets',
    icon: TreePine,
    distance: '35 km',
    image: 'https://images.pexels.com/photos/2887788/pexels-photo-2887788.jpeg?auto=compress&w=800',
  },
];

export function ExplorePreview() {
  return (
    <section 
      className="section-padding bg-emerald-950 text-white relative overflow-hidden" 
      id="discover"
      data-testid="explore-preview-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="overline text-gold mb-3">Discover</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-4">
            Explore <span className="text-emerald-400">Koraput</span>
          </h2>
          <p className="text-emerald-200 max-w-2xl mx-auto">
            Immerse yourself in the natural wonders and cultural heritage of Odisha's Eastern Ghats region.
          </p>
        </motion.div>

        {/* Destinations Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {destinations.map((dest, index) => (
            <motion.div
              key={dest.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group relative rounded-sm overflow-hidden"
              data-testid={`destination-card-${dest.id}`}
            >
              {/* Background Image */}
              <div className="aspect-[3/4] relative">
                <img
                  src={dest.image}
                  alt={dest.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-emerald-950/50 to-transparent" />
              </div>

              {/* Content */}
              <div className="absolute inset-0 flex flex-col justify-end p-6">
                <div className="flex items-center gap-2 mb-3">
                  <dest.icon className="w-5 h-5 text-gold" />
                  <span className="text-emerald-300 text-sm flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {dest.distance}
                  </span>
                </div>
                <h3 className="font-serif text-2xl text-white mb-2">{dest.name}</h3>
                <p className="text-emerald-200 text-sm mb-4 line-clamp-2">{dest.description}</p>
                <Link 
                  to={`/explore#${dest.id}`}
                  className="text-gold text-sm font-medium flex items-center gap-2 group-hover:gap-3 transition-all"
                >
                  Discover More <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-12"
        >
          <Link to="/explore">
            <Button
              variant="outline"
              className="border-gold text-gold hover:bg-gold hover:text-emerald-950"
              data-testid="plan-adventure-btn"
            >
              Plan Your Adventure
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
